import React, { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import BrandHeading from "@/components/brand/BrandHeading";
import BrandLabel from "@/components/brand/BrandLabel";
import BrandCard from "@/components/brand/BrandCard";
import SearchBar from "@/components/SearchBar";
import PropertyCard from "@/components/PropertyCard";
import BrandInput, { BrandField } from "@/components/brand/BrandInput";
import ResultsViewToggle from "@/components/search/ResultsViewToggle";
import LazyMapView from "@/components/search/LazyMapView";
import { fetchProperties } from "@/lib/api";

const CastlewoodSearch = () => {
  const [props, setProps] = useState([]);
  const [params, setParams] = useSearchParams();
  const q = params.get("q") || "";
  const minPrice = Number(params.get("min") || 0) || 0;
  const maxPrice = Number(params.get("max") || 0) || 0;

  // View state — defaults to List; Map view is lazy-loaded on demand.
  const [view, setView] = useState("list");

  useEffect(() => {
    fetchProperties({ brand: "castlewood", q: q || undefined, min_price: minPrice || undefined, max_price: maxPrice || undefined });
    fetchProperties({ brand: "castlewood" }).then(setProps);
  }, []);

  const filtered = useMemo(() => {
    const ql = q.toLowerCase();
    return props.filter((p) => {
      if (ql && !(p.name.toLowerCase().includes(ql) || p.location.toLowerCase().includes(ql) || p.country.toLowerCase().includes(ql))) return false;
      if (minPrice && p.price_from < minPrice) return false;
      if (maxPrice && p.price_from > maxPrice) return false;
      return true;
    });
  }, [props, q, minPrice, maxPrice]);

  const setQ = (val) => {
    const next = new URLSearchParams(params);
    if (val) next.set("q", val); else next.delete("q");
    setParams(next, { replace: true });
  };

  return (
    <div className="container-rail py-10 md:py-14" data-testid="castlewood-search">
      <BrandLabel withRule>Castlewood hotels</BrandLabel>
      <BrandHeading level={1} className="mt-4">The eight addresses.</BrandHeading>
      <p className="mt-3 max-w-[58ch] text-muted-foreground font-body">Browse the full collection. Filter by destination, season or price; each hotel remembers its own particulars.</p>
      <div className="mt-8"><SearchBar scope="castlewood" defaultBrand="castlewood" /></div>
      <div className="mt-10 grid lg:grid-cols-[280px_1fr] gap-10">
        <aside className="hidden lg:block" data-testid="castlewood-filters">
          <BrandCard className="p-5 space-y-5">
            <BrandLabel>Filter</BrandLabel>
            <BrandField label="Destination"><BrandInput data-testid="filter-destination" placeholder="e.g. Sylt, Quedlinburg…" defaultValue={q} onChange={(e) => setQ(e.target.value)} /></BrandField>
            <div className="grid grid-cols-2 gap-3">
              <BrandField label="Min €"><BrandInput data-testid="filter-min" type="number" min={0} defaultValue={minPrice || ""} onChange={(e) => { const next = new URLSearchParams(params); if (e.target.value) next.set("min", e.target.value); else next.delete("min"); setParams(next, { replace: true }); }} /></BrandField>
              <BrandField label="Max €"><BrandInput data-testid="filter-max" type="number" min={0} defaultValue={maxPrice || ""} onChange={(e) => { const next = new URLSearchParams(params); if (e.target.value) next.set("max", e.target.value); else next.delete("max"); setParams(next, { replace: true }); }} /></BrandField>
            </div>
          </BrandCard>
        </aside>
        <div>
          <div className="flex items-center justify-between mb-4">
            <span className="text-[12px] text-muted-foreground">{filtered.length} of {props.length} hotels</span>
            <ResultsViewToggle view={view} onChange={setView} />
          </div>
          {view === "map" ? (
            <div data-testid="castlewood-map-results">
              <LazyMapView items={filtered} initialBounds="dach" height={620} />
              <div className="mt-3 text-[12px] text-muted-foreground">
                Showing {filtered.length} {filtered.length === 1 ? "hotel" : "hotels"} across the DACH region.
              </div>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6" data-testid="castlewood-results">
              {filtered.map((p) => <PropertyCard key={p.id} item={p} />)}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
export default CastlewoodSearch;
